import { useState } from "react";
import { Link } from "react-router-dom";
import AfricaNavbar from "@/components/AfricaNavbar";
import AfricaFooter from "@/components/AfricaFooter";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import api from "@/lib/api";
import {
  Landmark, Building2, ShieldAlert, Car, Factory, Truck, Home, Ship, Briefcase,
  Send, CheckCircle2, Copy, ArrowRight,
} from "lucide-react";

const PATHWAYS = [
  { Icon: Landmark,   t: "Governments & Regulators",  b: "Statutory verification support · immutable evidence · executive dashboards" },
  { Icon: Building2,  t: "Banks & Financial Institutions", b: "Collateral · KYB · encumbrance · dispute evidence · signed webhooks" },
  { Icon: ShieldAlert,t: "Insurers",                   b: "Pre-underwriting · claim evidence · fraud triangulation · salvage" },
  { Icon: Car,        t: "Dealerships & Marketplaces", b: "Verified listings · buyer-protection unlocks · dealer subscriptions" },
  { Icon: Factory,    t: "Mining & Industrial",        b: "Equipment provenance · high-value asset passports" },
  { Icon: Truck,      t: "Fleet Operators",            b: "Pre-purchase inspections · portfolio dashboards · resale-readiness" },
  { Icon: Home,       t: "Real-estate Institutions",   b: "Title · encumbrance · deed · valuation-support evidence · escrow" },
  { Icon: Ship,       t: "Trade & Logistics",          b: "CustomsVerify™ · cross-border · bill-of-entry reconciliation" },
  { Icon: Briefcase,  t: "Corporate Procurement",      b: "SupplierVerify™ · ContractorVerify™ · transaction-level assurance" },
];

export default function AfricaEnterprise() {
  const [form, setForm] = useState({ org: "", name: "", email: "", country: "", industry: "", need: "" });
  const [busy, setBusy] = useState(false);
  const [ref, setRef] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/verification-requests", {
        country: form.country,
        industry: form.industry,
        category: "Bespoke Independent Verification",
        service_required: "Enterprise engagement",
        organisation: form.org,
        contact_name: form.name,
        email: form.email,
        description: form.need,
        urgency: "priority",
        source: "enterprise",
      });
      setRef(data.reference);
      toast.success(`Received · ${data.reference}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Please email enterprise@thomverify.africa");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <AfricaNavbar />
      <section className="max-w-[1500px] mx-auto px-6 py-16 lg:py-24">
        <div className="grid lg:grid-cols-[1.3fr_1fr] gap-14">
          <div>
            <div className="kicker text-white/60">Enterprise</div>
            <h1 className="display text-4xl lg:text-6xl mt-3 leading-[1.02]">Verified truth, delivered as enterprise infrastructure.</h1>
            <p className="mt-5 text-white/70 text-[15px] leading-[1.75] max-w-[640px]">
              THOM Verify™ operates as a shared trust engine. Banks, insurers, governments,
              regulators, dealers, mining operators and logistics platforms plug into the same
              APIs and receive the same signed evidence — with country-specific rules configured
              behind the scenes.
            </p>
            <div className="mt-10 grid md:grid-cols-2 gap-3">
              {PATHWAYS.map((p, i) => (
                <div key={i} className="border border-white/10 bg-white/[0.02] p-5" data-testid={`africa-enterprise-pathway-${i}`}>
                  <p.Icon className="h-5 w-5 text-[#B08C4A]" />
                  <h3 className="mt-4 text-[15px] font-semibold">{p.t}</h3>
                  <p className="text-[12.5px] text-white/60 mt-1.5 leading-[1.65]">{p.b}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Request Verification form */}
          <div id="request" className="lg:sticky lg:top-24 self-start">
            <div className="border border-white/15 bg-white/[0.03] p-6 lg:p-8" data-testid="africa-enterprise-request-form">
              {ref ? (
                <div data-testid="ent-success">
                  <CheckCircle2 className="h-8 w-8 text-emerald-400" />
                  <div className="kicker text-white/60 mt-4">Enterprise request received</div>
                  <h3 className="text-2xl font-semibold mt-2 leading-tight">Reference issued.</h3>
                  <p className="mt-3 text-[13.5px] text-white/70 leading-[1.7]">Your enterprise verification enquiry has been recorded. Our team will contact you within one business day. Track progress with the reference below.</p>
                  <div className="mt-4 flex items-center gap-2 flex-wrap">
                    <div className="border border-white/15 bg-black/40 px-3 py-2 mono uppercase tracking-[0.22em] text-[15px] text-[#B08C4A]">{ref}</div>
                    <Button variant="outline" size="sm" className="border-white/20 text-white hover:bg-white/10 hover:text-white" onClick={() => { navigator.clipboard.writeText(ref); toast.success("Reference copied"); }}><Copy className="h-3.5 w-3.5 mr-1" /> Copy</Button>
                  </div>
                  <Link to={`/track-verification?ref=${ref}`} className="mt-4 inline-flex" data-testid="ent-track-link">
                    <Button className="bg-[#B08C4A] hover:bg-[#8f6f34] text-black">Track your request <ArrowRight className="ml-1.5 h-4 w-4" /></Button>
                  </Link>
                </div>
              ) : (
              <>
              <div className="kicker text-white/60">Request enterprise verification</div>
              <h3 className="text-2xl font-semibold mt-2 leading-tight">Tell us what you need to verify.</h3>
              <form onSubmit={submit} className="mt-6 space-y-4">
                <Field label="Organisation *"><Input required value={form.org}     onChange={(e)=>setForm({...form, org: e.target.value})}     className="ent-input" data-testid="ent-org" /></Field>
                <Field label="Contact name *"> <Input required value={form.name}    onChange={(e)=>setForm({...form, name: e.target.value})}    className="ent-input" data-testid="ent-name" /></Field>
                <Field label="Work email *">   <Input required type="email" value={form.email} onChange={(e)=>setForm({...form, email: e.target.value})} className="ent-input" data-testid="ent-email" /></Field>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Country *"><Input required value={form.country}  onChange={(e)=>setForm({...form, country: e.target.value})}  className="ent-input" data-testid="ent-country" /></Field>
                  <Field label="Industry"><Input value={form.industry}          onChange={(e)=>setForm({...form, industry: e.target.value})} className="ent-input" data-testid="ent-industry" /></Field>
                </div>
                <Field label="What do you need to verify? *">
                  <textarea
                    required rows={4} value={form.need}
                    onChange={(e)=>setForm({...form, need: e.target.value})}
                    className="w-full bg-white/5 border border-white/20 text-white placeholder:text-white/40 rounded-sm p-2.5 text-sm focus:ring-2 focus:ring-white/40 focus:outline-none"
                    data-testid="ent-need"
                    placeholder="Assets · organisations · transactions · scale · timing…"
                  />
                </Field>
                <Button type="submit" disabled={busy} className="w-full bg-[#B08C4A] hover:bg-[#8f6f34] text-black h-11" data-testid="ent-submit">
                  {busy ? "Submitting…" : (<><Send className="mr-2 h-4 w-4" />Submit request</>)}
                </Button>
                <p className="text-[11px] text-white/50 text-center">Every submission creates a TV-AFR reference and is auto-routed to THOM Verify Operations for regional allocation.</p>
              </form>
              </>
              )}
            </div>
          </div>
        </div>
      </section>
      <AfricaFooter />

      <style>{`
        .ent-input { background: rgba(255,255,255,0.05); border-color: rgba(255,255,255,0.2); color: #fff; }
        .ent-input::placeholder { color: rgba(255,255,255,0.4); }
      `}</style>
    </div>
  );
}

const Field = ({ label, children }) => (
  <div>
    <label className="mono text-[10.5px] uppercase tracking-[0.22em] text-white/50">{label}</label>
    <div className="mt-1.5">{children}</div>
  </div>
);
